import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CategoriaContaPagarEnum, CategoriaContaPagarLabel } from '../../../../../../core/enums/categoria-conta-pagar.enum';
import { CategoriaContaReceberEnum, CategoriaContaReceberLabel } from '../../../../../../core/enums/categoria-conta-receber.enum';
import { FinanceiroService } from '../../../../services/financeiro.service';
import { Orcamento } from '../../../models/orcamento';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from "ngx-toastr";
import Swal from 'sweetalert2';
import { forkJoin } from 'rxjs';

import { AuthService } from '../../../../../auth/services/auth-service';

@Component({
  selector: 'app-financeiro-orcamento',
  templateUrl: './financeiro-orcamento.component.html',
  styleUrls: ['./financeiro-orcamento.component.css'],
  standalone: false
})
export class FinanceiroOrcamentoComponent implements OnInit {
  mesAtual: number = new Date().getMonth();
  anoAtual: number = new Date().getFullYear();
  meses: string[] = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  tiposConta: string[] = Array.from(CategoriaContaPagarLabel.values());
  tiposReceita: string[] = Array.from(CategoriaContaReceberLabel.values());

  modalNovoOrcamento = false;
  modalNovoOrcamentoReceita = false;
  modoEdicao = false;
  itemSelecionadoId: number | null = null;

  novoOrcamento = {
    mes: new Date().getMonth() + 1,
    semana: 1,
    tipoConta: '',
    valor: 0
  };

  novoOrcamentoReceita = {
    mes: new Date().getMonth() + 1,
    semana: 1,
    tipoReceita: '',
    valor: 0
  };

  orcamento: any = {
    renda: 0,
    saldoAtual: 0,
    semanas: []
  };

  constructor(
    private financeiroService: FinanceiroService,
    private http: HttpClient,
    private toastrService: ToastrService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.carregarOrcamentos();
  }

  carregarOrcamentos() {
    const primeiroDia = new Date(this.anoAtual, this.mesAtual, 1);
    const dataFormatada = primeiroDia.toISOString();

    forkJoin({
      orcamentos: this.financeiroService.obterOrcamentos(dataFormatada),
      contasPagar: this.financeiroService.obterContasAPagar(),
      contasReceber: this.financeiroService.obterContasAReceber()
    }).subscribe(res => {
      if (res.orcamentos.sucesso && res.orcamentos.dados) {
        this.processarOrcamentos(
          res.orcamentos.dados,
          res.contasPagar.sucesso ? res.contasPagar.dados : [],
          res.contasReceber.sucesso ? res.contasReceber.dados : []
        );
      }
    });
  }

  getSemanaDoMes(data: Date): number {
    const dia = data.getDate();
    if (dia <= 7) return 1;
    if (dia <= 14) return 2;
    if (dia <= 21) return 3;
    return 4;
  }

  processarOrcamentos(dados: Orcamento[], contasPagar: any[] = [], contasReceber: any[] = []) {
    const semanasMap = new Map<number, any>();

    // Inicializa 4 semanas por padrão
    for (let i = 1; i <= 4; i++) {
      semanasMap.set(i, {
        titulo: `${i}ª SEMANA`,
        itens: []
      });
    }

    let renda = 0;
    let totalGasto = 0;

    // Processar Contas Pagas e Recebidas para preencher o "Gasto"
    const gastosPorSemanaCategoria = new Map<string, number>();

    const filtrarEProcessarContas = (contas: any[], tipo: number) => {
      contas.forEach(conta => {
        if (conta.pago && conta.dataPagamento) {
          const dataPagamento = new Date(conta.dataPagamento);
          if (dataPagamento.getMonth() === this.mesAtual && dataPagamento.getFullYear() === this.anoAtual) {
            const semana = this.getSemanaDoMes(dataPagamento);
            const chave = `${semana}-${tipo}-${conta.categoria}`;
            const valorAtual = gastosPorSemanaCategoria.get(chave) || 0;
            gastosPorSemanaCategoria.set(chave, valorAtual + conta.valor);
          }
        }
      });
    };

    filtrarEProcessarContas(contasPagar || [], 1);
    filtrarEProcessarContas(contasReceber || [], 2);

    const chavesProcessadas = new Set<string>();

    dados.forEach(item => {
      const semanaNum = item.semana || 1;
      if (!semanasMap.has(semanaNum)) {
        semanasMap.set(semanaNum, {
          titulo: `${semanaNum}ª SEMANA`,
          itens: []
        });
      }

      const label = item.tipo === 1
        ? CategoriaContaPagarLabel.get(item.categoria)
        : CategoriaContaReceberLabel.get(item.categoria);

      const chaveGasto = `${semanaNum}-${item.tipo}-${item.categoria}`;
      const gastoRealizado = gastosPorSemanaCategoria.get(chaveGasto) || 0;
      chavesProcessadas.add(chaveGasto);

      semanasMap.get(semanaNum).itens.push({
        id: item.id,
        item: label || 'Outros',
        planejado: item.valor,
        gasto: gastoRealizado,
        tipo: item.tipo,
        mes: item.mes,
        semana: item.semana,
        categoria: item.categoria
      });

      if (item.tipo === 2) {
        renda += item.valor;
      } else if (item.tipo === 1) {
        totalGasto += item.valor;
      }
    });

    // Adicionar gastos que não possuem orçamento planejado
    gastosPorSemanaCategoria.forEach((valor, chave) => {
      if (!chavesProcessadas.has(chave)) {
        const [semanaStr, tipoStr, categoriaStr] = chave.split('-');
        const semanaNum = parseInt(semanaStr);
        const tipo = parseInt(tipoStr);
        const categoria = parseInt(categoriaStr);

        const label = tipo === 1
          ? CategoriaContaPagarLabel.get(categoria)
          : CategoriaContaReceberLabel.get(categoria);

        if (!semanasMap.has(semanaNum)) {
          semanasMap.set(semanaNum, {
            titulo: `${semanaNum}ª SEMANA`,
            itens: []
          });
        }

        semanasMap.get(semanaNum).itens.push({
          id: 0,
          item: label || 'Outros',
          planejado: 0,
          gasto: valor,
          tipo: tipo,
          mes: this.meses[this.mesAtual],
          semana: semanaNum,
          categoria: categoria
        });
      }
    });

    this.orcamento.renda = renda;
    this.orcamento.saldoAtual = renda - totalGasto;
    this.orcamento.semanas = Array.from(semanasMap.values()).sort((a, b) => a.titulo.localeCompare(b.titulo));
  }

  mesAnterior() {
    if (this.mesAtual === 0) {
      this.mesAtual = 11;
      this.anoAtual--;
    } else {
      this.mesAtual--;
    }
    this.carregarOrcamentos();
  }

  proximoMes() {
    if (this.mesAtual === 11) {
      this.mesAtual = 0;
      this.anoAtual++;
    } else {
      this.mesAtual++;
    }
    this.carregarOrcamentos();
  }

  abrirModalNovoOrcamento() {
    this.novoOrcamento = {
      mes: this.mesAtual + 1,
      semana: 1,
      tipoConta: '',
      valor: 0
    };
    this.modalNovoOrcamento = true;
  }

  abrirModalNovoOrcamentoReceita() {
    this.novoOrcamentoReceita = {
      mes: this.mesAtual + 1,
      semana: 1,
      tipoReceita: '',
      valor: 0
    };
    this.modalNovoOrcamentoReceita = true;
  }

  fecharModal() {
    this.modalNovoOrcamento = false;
    this.modalNovoOrcamentoReceita = false;
    this.modoEdicao = false;
    this.itemSelecionadoId = null;
  }

  abrirModalDetalhe(item: any) {
    this.modoEdicao = true;
    this.itemSelecionadoId = item.id;

    if (item.tipo === 1) { // Despesa
      this.novoOrcamento = {
        id: item.id,
        mes: this.meses.indexOf(item.mes) + 1,
        semana: item.semana,
        tipoConta: item.item,
        valor: item.planejado
      } as any;
      this.modalNovoOrcamento = true;
    } else { // Receita
      this.novoOrcamentoReceita = {
        id: item.id,
        mes: this.meses.indexOf(item.mes) + 1,
        semana: item.semana,
        tipoReceita: item.item,
        valor: item.planejado
      } as any;
      this.modalNovoOrcamentoReceita = true;
    }
  }

  deletarOrcamento(id: number) {
    Swal.fire({
      title: 'Deseja realmente excluir este orçamento?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim',
      confirmButtonColor: '#fd7e14',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.financeiroService.deletarOrcamento(id).subscribe(res => {
          if (res.sucesso) {
            this.toastrService.success('Orçamento excluído com sucesso!');
            this.carregarOrcamentos();
            this.fecharModal();
          } else {
            this.toastrService.error('Erro ao excluir orçamento');
          }
        });
      }
    });
  }

  salvarOrcamento() {
    const request: any = {
      usuario: this.authService.AccessToken?.nome || 'string',
      empresaId: this.authService.AccessToken?.origem || 0,
      mes: this.meses[this.novoOrcamento.mes - 1],
      semana: this.novoOrcamento.semana,
      categoria: Array.from(CategoriaContaPagarLabel.entries()).find(([k, v]) => v === this.novoOrcamento.tipoConta)?.[0] || 0,
      valor: this.novoOrcamento.valor,
      tipo: 1, // Despesa/A Pagar
      dataInicio: new Date(this.anoAtual, this.novoOrcamento.mes - 1, 1).toISOString(),
      dataFim: new Date(this.anoAtual, this.novoOrcamento.mes - 1, 28).toISOString(), // Ajuste conforme necessário
      id: this.itemSelecionadoId || 0
    };

    const call = this.modoEdicao
      ? this.financeiroService.alterarOrcamento(request)
      : this.financeiroService.salvarOrcamento(request);

    call.subscribe(res => {
      if (res.sucesso) {
        this.toastrService.success(this.modoEdicao ? 'Orçamento atualizado!' : 'Orçamento salvo!');
        this.carregarOrcamentos();
        this.fecharModal();
      } else {
        this.toastrService.error('Erro ao salvar orçamento');
      }
    });
  }

  salvarOrcamentoReceita() {
    const request: any = {
      usuario: this.authService.AccessToken?.nome || 'string',
      empresaId: this.authService.AccessToken?.origem || 0,
      mes: this.meses[this.novoOrcamentoReceita.mes - 1],
      semana: this.novoOrcamentoReceita.semana,
      categoria: Array.from(CategoriaContaReceberLabel.entries()).find(([k, v]) => v === this.novoOrcamentoReceita.tipoReceita)?.[0] || 0,
      valor: this.novoOrcamentoReceita.valor,
      tipo: 2, // Receita
      dataInicio: new Date(this.anoAtual, this.novoOrcamentoReceita.mes - 1, 1).toISOString(),
      dataFim: new Date(this.anoAtual, this.novoOrcamentoReceita.mes - 1, 28).toISOString(),
      id: this.itemSelecionadoId || 0
    };

    const call = this.modoEdicao
      ? this.financeiroService.alterarOrcamento(request)
      : this.financeiroService.salvarOrcamento(request);

    call.subscribe(res => {
      if (res.sucesso) {
        this.toastrService.success(this.modoEdicao ? 'Receita atualizada!' : 'Receita salva!');
        this.carregarOrcamentos();
        this.fecharModal();
      } else {
        this.toastrService.error('Erro ao salvar receita');
      }
    });
  }

  calcularTotalPlanejado(semana: any): number {
    return semana.itens.reduce((acc: number, item: any) => {
      return item.tipo === 2 ? acc + item.planejado : acc - item.planejado;
    }, 0);
  }

  calcularTotalGasto(semana: any): number {
    return semana.itens.reduce((acc: number, item: any) => {
      return item.tipo === 2 ? acc + item.gasto : acc - item.gasto;
    }, 0);
  }
}
